import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as z from 'zod';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const inquirySchema = z.object({
  examNumber: z.string().min(5),
  birthDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format'),
});

type InquiryFormValues = z.infer<typeof inquirySchema>;

export const AdmissionStatusPage = () => {
  const navigate = useNavigate();
  const [result, setResult] = React.useState<'success' | 'fail' | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<InquiryFormValues>({
    resolver: zodResolver(inquirySchema) as unknown as Resolver<InquiryFormValues>,
  });

  const onSubmit = async (data: InquiryFormValues) => {
    // Simulate API call
    console.log(data);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    // Mock result randomly
    setResult(Math.random() > 0.5 ? 'success' : 'fail');
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Admission Status Inquiry</CardTitle>
          <CardDescription>Enter your exam number and date of birth to check your results.</CardDescription>
        </CardHeader>
        <CardContent>
          {!result ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="examNumber">Exam Number</Label>
                <Input id="examNumber" placeholder="12345" {...register('examNumber')} />
                {errors.examNumber && <p className="text-sm text-red-500">Required</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="birthDate">Date of Birth</Label>
                <Input id="birthDate" type="date" {...register('birthDate')} />
                {errors.birthDate && <p className="text-sm text-red-500">Required</p>}
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Checking...' : 'Check Status'}
              </Button>
            </form>
          ) : result === 'success' ? (
            <div className="text-center space-y-4">
              <div className="text-4xl">🌸</div>
              <h2 className="text-xl font-bold text-green-600">Congratulations!</h2>
              <p>You have passed the entrance examination.</p>
              <Button className="w-full" onClick={() => navigate('/enrollment')}>
                Proceed to Enrollment
              </Button>
            </div>
          ) : (
            <div className="text-center space-y-4">
              <div className="text-4xl">📝</div>
              <h2 className="text-xl font-bold text-gray-600">Result</h2>
              <p>We regret to inform you that you were not selected.</p>
              <Button variant="outline" className="w-full" onClick={() => setResult(null)}>
                Check Another
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
